import { components, useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";

/** The active tenant's settings, as `GET /v2/tenant-settings` returns them. */
export type TenantSettings = components["schemas"]["TenantSettings"];

/** Cache key of the tenant-settings read; a save writes its result here. */
export const TENANT_SETTINGS_QUERY_KEY = ["tenantSettings"] as const;

/**
 * The active tenant's settings. Follows the app's query defaults: another
 * user's change shows on reload, or once the query is 5 minutes old and a
 * reader mounts.
 */
export function useTenantSettings() {
  const apiClient = useApi();

  return useQuery({
    queryKey: TENANT_SETTINGS_QUERY_KEY,
    meta: {
      errorMessage: "Failed to load the tenant's settings"
    },
    queryFn: async (): Promise<TenantSettings> => {
      const response = await apiClient.GET("/v2/tenant-settings");
      const result = response.data?.result;
      if (!result) {
        throw new Error("Missing result in tenant settings response");
      }
      return result;
    }
  });
}
